/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { Event } from '../models/event';
import { EmGroup } from './../models/em-group';
import { GroupType } from './../models/group-type';

import { EventDialogComponent } from './event-dialog.component';
import { EventCreationDialogComponent } from './event-creation-dialog.component';
import { ConfirmDeleteDialogComponent } from "./confirm-delete-dialog.component";
import { AddToGroupDialogComponent } from "./add-to-group-dialog.component";
import { ManageGroupsDialogComponent } from "./manage-groups-dialog.component";
import { RemoveFromGroupDialogComponent } from "./remove-from-group-dialog.component";
import { ShowGroupDialogComponent } from "./show-group-dialog.component";

import { EventService } from '../services/event.service';
import { GroupService } from '../services/group.service';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from './simple-success-error-dialog-box/simple-success-error-dialog-box.component';

@Component({
  templateUrl: './events.component.html'
})
export class EventsComponent implements OnInit {

    @ViewChild('eventDialog')
    eventDialog: EventDialogComponent;
    
    @ViewChild('eventCreationDialog')
    eventCreationDialog: EventCreationDialogComponent;

    @ViewChild('confirmDeleteDialog')
    confirmDialog: ConfirmDeleteDialogComponent;
    
    @ViewChild('showGroupDialog')
    showGroupsDialog: ShowGroupDialogComponent;
    
    @ViewChild('addToGroupDialog')
    addToGroupDialogComponent: AddToGroupDialogComponent;
    
    @ViewChild('removeFromGroupDialog')
    removeFromGroupDialogComponent: RemoveFromGroupDialogComponent;
    
    @ViewChild('manageGroupsDialog')
    manageGroupsDialog: ManageGroupsDialogComponent;

    /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;
    
    event: Event;
    events: EventUI[] = [];
    
    constructor(
        private eventService: EventService,
        private groupService: GroupService
    ) {}

    ngOnInit(): void {
        this.loadEvents();
    }

    isEmptySelection(): boolean {
        return this.events.filter(event => event.selected).length == 0;
    }
    
    isSingleSelection(): boolean {
        return this.events.filter(event => event.selected).length == 1;
    }
    
    isMultipleSelection(): boolean {
        return this.events.some(event => event.selected);
    }

    isAllSelected(): boolean {
        return this.events.every(event => event.selected);
    }
    
    selectAll(event: any): void {
        this.events.forEach(ev => ev.selected = event.target.checked);
    }

    loadEvents(): void {
        const eventList: EventUI[] = [];

        this.eventService.getAllEvents().then(
            response => {
                for (const event of response) {
                    const eventUI = new EventUI();
                    eventUI.event = event;
                    eventList.push(eventUI);
                }
                
                this.events = eventList;
            },
            error => {
                console.error('EventsComponent: ', error);
            }
        );
    }

    onCreate(): void {
        this.event = new Event();
        this.eventDialog.show();
    }

    editEvent(id: number) {
        //
        // retrieve event and pass to modal
        this.eventService.getEventById(id).then(
            response => {
                this.event = response;
                this.eventDialog.show();
            }
        );
    }

    createOrUpdateEvent(): void {
        if (this.event.id == null) {
            this.eventService.createEvent(this.event).then(
                response => {
                    this.loadEvents();
                    this.eventDialog.hide();
                },
                error => { console.log("Error: " + error); }
            );
        }
        else {
            this.eventService.updateEvent(this.event).then(
                response => {
                    this.loadEvents();
                    this.eventDialog.hide();
                },
                error => { console.log("Error: " + error); }
            );
        }
    }

   onDelete(): void {
        if (!this.isEmptySelection()) {
            const names: string[] = [];
            this.events.filter(ui => ui.selected).forEach(ui => names.push(ui.event.name));
            this.confirmDialog.show("Trigger", names);
        }
    }
    
    
    
    deleteEvents(): void {
        this.eventService.deleteEvents(this.getSelectedEventIds()).then(
            response => {
                this.loadEvents();
            },
            error => {
                this.handleError(error);
            }
        );
    }

    onEdit(): void {
        if (this.isSingleSelection()) {
            this.editEvent(this.events[0].event.id);
        }
    }
    
    onGenerate(): void {
        //
        // retrieve event and pass to modal
        const events = this.events.filter(event => event.selected);

        this.eventService.getEventById(events[0].event.id).then(
            response => {
                this.event = response;
                this.eventCreationDialog.event = response;
                this.eventCreationDialog.show();
            },
            error => {
                console.log(error);
            }
        );
    }
    
    onAddGroups(): void {
         if (!this.isEmptySelection()) {
            this.addToGroupDialogComponent.show(GroupType.EVENT, this.getSelectedEventNames());
         }
    }
    
    addToGroup(group: EmGroup): void {
        this.groupService.addItemsToGroup(group.id, this.getSelectedEventIds()).then(
            success => {
                this.loadEvents();
            },
            error => {
                this.handleError(error);
            }
        );
    }    

    onRemoveGroup(): void {
        if (!this.isEmptySelection()) {
            const eventNames: string[] = [];
            const groupNames: string[] = [];
            const groups: EmGroup[] = [];
            
            this.getSelectedEvents().forEach(
                event => {
                    eventNames.push(event.name);
                    
                    if (event.groups != null) {
                        event.groups.forEach(
                            group => {
                                if (!groupNames.includes(group.name)) {
                                    groupNames.push(group.name);
                                    groups.push(group);
                                }
                            }
                        );
                    }
                }
            );

            this.removeFromGroupDialogComponent.show(groups, eventNames);
        }
    }
    
    removeFromGroup(group: EmGroup): void {
        this.groupService.removeItemsFromGroup(group.id, this.getSelectedEventIds()).then(
            success => {
                this.loadEvents();
            },
            error => {
                this.handleError(error);
            }
        );
    }
    
    
    getSelectedEvents(): Event[] {
        const events: Event[] = [];
        this.events.filter(ui => ui.selected).forEach(ui => events.push(ui.event));
        return events;
    }
    
    getSelectedEventIds(): number[] {
        const ids: number[] = [];
        this.events.filter(ui => ui.selected).forEach(ui => ids.push(ui.event.id));
        return ids;
    }

    getSelectedEventNames(): string[] {
        const names: string[] = [];
        this.events.filter(ui => ui.selected).forEach(ui => names.push(ui.event.name));

        return names;
    }

    handleError(error: any): void {
        console.error("EventsComponent.handleError() - Error: " + error);
        if (error != null) {
            const response: Response = error;
            // Show the error in the SimpleSuccessErrorDialogComponent
            this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
        }
    }

}

class EventUI {
    selected = false;
    event: Event;
}
